import { type Editor as TiptapEditor } from "@tiptap/react";
import {
  ForwardedRef,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import {
  applyPlainTextDraft,
  RichTextInput,
  RichTextInputRef,
} from "@/components/organisms/RichTextEditor";
import { State } from "@/state";
import { threadNodeIdToTabId } from "@/state/threadTabs";

export interface ComposerProps {
  /** Sends the prompt; ``false`` refuses it and keeps what was typed. */
  onSubmit: (
    serializedData: Array<{ text?: string; id?: string }>,
  ) => boolean | void;
  /** Escape from the composer (e.g. return focus to the triage row). */
  onEscape?: () => void;
}

export interface ComposerRef {
  submit: () => void;
  focus: () => void;
}

type ImplProps = ComposerProps & { __forwardedRef?: ForwardedRef<ComposerRef> };

function TiptapThreadComposerImpl({
  onSubmit,
  onEscape,
  __forwardedRef,
}: ImplProps) {
  const richRef = useRef<RichTextInputRef>(null);
  const editorRef = useRef<TiptapEditor | null>(null);
  const pendingDraftRef = useRef<string | null>(null);
  const composerDraft = State((state) => state.composerDraft);
  const setComposerDraft = State((state) => state.setComposerDraft);
  // ``ThreadBox`` is keyed on the thread, so the composer remounts per tab and
  // this is the tab whose unsent draft it keeps.
  const activeThreadNodeId = State((state) => state.activeThreadNodeId);
  const tabId = threadNodeIdToTabId(activeThreadNodeId);
  const setThreadDraft = State((state) => state.setThreadDraft);

  const clearPendingDraft = useCallback(() => {
    pendingDraftRef.current = null;
    setComposerDraft(null);
  }, [setComposerDraft]);

  const applyPendingDraft = useCallback(
    (editor?: TiptapEditor) => {
      const draft = pendingDraftRef.current ?? composerDraft;
      if (!draft) return true;

      if (editor) {
        applyPlainTextDraft(editor, draft);
        clearPendingDraft();
        return true;
      }

      const applied = richRef.current?.setDraft(draft) ?? false;
      if (applied) {
        clearPendingDraft();
      }
      return applied;
    },
    [clearPendingDraft, composerDraft],
  );

  useEffect(() => {
    if (composerDraft == null) return;
    pendingDraftRef.current = composerDraft;

    if (applyPendingDraft()) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;

    const retry = () => {
      if (cancelled || attempts >= maxAttempts) return;
      attempts += 1;
      if (applyPendingDraft()) return;
      window.setTimeout(retry, 50);
    };

    window.setTimeout(retry, 0);

    return () => {
      cancelled = true;
    };
  }, [applyPendingDraft, composerDraft]);

  const handleEditorCreate = useCallback(
    (editor: TiptapEditor) => {
      editorRef.current = editor;
      // Bring back what was typed before this tab was left.
      const savedDraft = State.getState().threadDrafts[tabId];
      if (savedDraft) {
        editor.commands.setContent(savedDraft);
      }
      applyPendingDraft(editor);
    },
    [applyPendingDraft, tabId],
  );

  const handleSubmit = useCallback(() => {
    const serializedArray = richRef.current?.getSerializedArray?.() || [];
    // Check if there's any actual content (text or references)
    const hasContent = serializedArray.some(
      (item) => (item.text && item.text.trim()) || item.id,
    );

    // An empty prompt is the host's to refuse: a message carrying the files
    // dropped on it needs no words.
    if (onSubmit(hasContent ? serializedArray : []) === false || !hasContent) {
      return;
    }
    richRef.current?.clear?.();
    // Clearing does not emit an update, so drop the sent draft explicitly.
    setThreadDraft(tabId, null);
    richRef.current?.focusEnd?.();
  }, [onSubmit, setThreadDraft, tabId]);

  useImperativeHandle(
    __forwardedRef,
    () => ({
      submit: handleSubmit,
      focus: () => richRef.current?.focusEnd?.(),
    }),
    [handleSubmit],
  );

  return (
    <div className="w-full">
      <RichTextInput
        ref={richRef}
        triggers={["@"]}
        enableContextInline
        onUpdateText={() => {
          const editor = editorRef.current;
          if (editor) {
            setThreadDraft(tabId, editor.isEmpty ? null : editor.getJSON());
          }
        }}
        onMetaEnter={handleSubmit}
        onEscape={onEscape}
        onEditorCreate={handleEditorCreate}
        blockEnter={false}
        // Do NOT autofocus on mount. The Thread composer can be remounted by
        // unrelated state changes (e.g. switching the active panel node), and
        // the Tiptap ``onCreate`` hook would then steal focus from whatever
        // the user just clicked — most visibly causing table cells to defocus
        // on click while the side panel was open. Focus is still acquired
        // when the user clicks into the composer (wrapper handles that), and
        // ``focusEnd`` is invoked imperatively after submit.
        autoFocus={false}
        style={{
          // Room for a few lines before it grows, without reading as a big
          // empty block under the pending changes tray.
          minHeight: "80px",
        }}
      />
    </div>
  );
}

const ThreadComposer = forwardRef<ComposerRef, ComposerProps>((props, ref) => {
  return <TiptapThreadComposerImpl {...props} __forwardedRef={ref} />;
}) as ForwardRefExoticComponent<ComposerProps & RefAttributes<ComposerRef>>;

ThreadComposer.displayName = "ThreadComposer";

export { ThreadComposer };
